import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { tasksAPI, syncAPI, statsAPI, exportAPI } from '../lib/api';
import {
  LogOut, RefreshCw, BarChart3, FileSpreadsheet, Sparkles, Filter,
  Search, Users, FileText, Plus, AlertTriangle, X, Bell, Map,
  Globe, CheckCircle2, ChevronDown, Activity, Sun, Moon
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import MapView from '../components/MapView';
import TasksList from '../components/TasksList';
import TaskDetails from '../components/TaskDetails';
import TaskDetailsErrorBoundary from '../components/TaskDetailsErrorBoundary';
import StatsPanel from '../components/StatsPanel';
import FiltersPanel from '../components/FiltersPanel';
import AIReportModal from '../components/AIReportModal';
import BulkActionsBar from '../components/BulkActionsBar';
import UserManagement from '../components/UserManagement';
import ApiLogsViewer from '../components/ApiLogsViewer';
import ConfirmDialog from '../components/ConfirmDialog';
import CreateIncidentModalV2 from '../components/CreateIncidentModalV2';
import ArcGISSyncStatus from '../components/ArcGISSyncStatus';
import NotificationBell from '../components/NotificationBell';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import CriticalZonesWidget from '../components/CriticalZonesWidget';
import MapFiltersBar from '../components/MapFiltersBar';
import { downloadBlob } from '../lib/utils';
import { useDarkMode } from '../lib/useDarkMode';

export default function DashboardPage({ user, onLogout }) {
  const [selectedTask, setSelectedTask] = useState(null);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showAIReportModal, setShowAIReportModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showApiLogs, setShowApiLogs] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showArcGISSyncConfirm, setShowArcGISSyncConfirm] = useState(false);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [dismissedAlert, setDismissedAlert] = useState(false);
  const [mapFilters, setMapFilters] = useState({ tipo: '', date_from: '', date_to: '' });
  const [showMapFilters, setShowMapFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [includeCompleted, setIncludeCompleted] = useState(false); // Estado para incluir/excluir completadas
  // Derived filter key — when it changes, page automatically resets to 1
  const filterKey = `${JSON.stringify(filters)}|${searchQuery}|${includeCompleted}`;
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  const currentPage = pageState.key === filterKey ? pageState.page : 1;

  const setCurrentPage = useCallback((newPage) => {
    setPageState(prev => ({
      key: filterKey,
      page: typeof newPage === 'function' ? newPage(prev.key === filterKey ? prev.page : 1) : newPage,
    }));
  }, [filterKey]);

  const PAGE_SIZE = 50;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDark, setIsDark] = useDarkMode();
  // Track aggressive polling while a manual sync is in progress
  const [syncPolling, setSyncPolling] = useState(false);
  const preSyncTimestampRef = useRef(null);

  const hasMapFilters = !!(mapFilters.tipo || mapFilters.date_from || mapFilters.date_to);

  // Separate query for map — fetches up to 500 tasks when map filters are active
  const { data: mapTasksData } = useQuery({
    queryKey: ['map-tasks', mapFilters],
    queryFn: () => tasksAPI.getTasks({
      per_page: 500, page: 1, include_completed: true,
      ...(mapFilters.tipo ? { tipo: mapFilters.tipo } : {}),
      ...(mapFilters.date_from ? { date_from: mapFilters.date_from } : {}),
      ...(mapFilters.date_to ? { date_to: mapFilters.date_to } : {}),
    }).then(r => r.data),
    enabled: hasMapFilters,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch ALL tasks (unfiltered) to check for high priority alerts
  const { data: allTasksData } = useQuery({
    queryKey: ['all-tasks-for-alerts'],
    queryFn: () => tasksAPI.getTasks({ per_page: 1000 }).then(res => res.data),
    refetchInterval: 60000, // Check every minute
  });

  // Fetch tasks with current filters
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', filters, searchQuery, includeCompleted, currentPage],
    queryFn: () => tasksAPI.getTasks({
      ...filters,
      search: searchQuery,
      include_completed: includeCompleted,
      page: currentPage,
      per_page: PAGE_SIZE,
    }).then(res => res.data),
    refetchInterval: 300000,
    placeholderData: (prev) => prev,  // React Query v5 replacement for keepPreviousData
  });

  // Live version of selectedTask: re-derived from the tasks query cache so the
  // open TaskDetails panel reflects updates (e.g. prioridad_manual) without needing
  // to close/reopen it. Falls back to the raw selectedTask if not found in current page.
  const liveSelectedTask = useMemo(() => {
    if (!selectedTask) return null;
    const fromTasks = tasksData?.data?.find(t => t.id === selectedTask.id);
    const fromMapTasks = mapTasksData?.data?.find(t => t.id === selectedTask.id);
    const fromAllTasks = allTasksData?.data?.find(t => t.id === selectedTask.id);
    return fromTasks || fromMapTasks || fromAllTasks || selectedTask;
  }, [selectedTask, tasksData, mapTasksData, allTasksData]);

  // Helper to safely check priority - handles numbers, strings, and SCMejora objects
  const checkPriority = useCallback((task, num, ...strings) => {
    const p = task.prioridad_num || task.prioridad;
    if (p === num || p === String(num)) return true;
    if (typeof p === 'object' && p !== null) {
      const code = (p.codigo || p.nombre || '').toLowerCase();
      return strings.some(s => code === s);
    }
    if (typeof p === 'string') {
      const lower = p.toLowerCase();
      return strings.some(s => lower === s);
    }
    return false;
  }, []);

  // Calculate high priority tasks count
  const highPriorityTasks = useMemo(() => {
    if (!allTasksData?.data) return { count: 0, p4: 0, p5: 0, alta: 0, muy_alta: 0 };
    const tasks = allTasksData.data;
    
    const p4 = tasks.filter(t => checkPriority(t, 4, 'alta')).length;
    const p5 = tasks.filter(t => checkPriority(t, 5, 'muy_alta', 'urgente', 'critica')).length;
    return { count: p4 + p5, p4, p5 };
  }, [allTasksData, checkPriority]);

  // Show alert when high priority tasks exist
  const showHighPriorityAlert = highPriorityTasks.count > 0 && !dismissedAlert;

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => statsAPI.getStatistics().then(res => res.data),
    refetchInterval: 60000,
  });

  // Sync status — refreshed after manual sync trigger
  const { data: syncStatusData, refetch: refetchSyncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => syncAPI.getSyncStatus().then(res => res.data),
    // Poll every 8s while waiting for sync to finish, otherwise every 2 min
    refetchInterval: syncPolling ? 8000 : 120000,
    staleTime: syncPolling ? 0 : 60000,
  });

  // Safety timeout: stop aggressive polling after 15 min
  useEffect(() => {
    if (!syncPolling) return;
    const timer = setTimeout(() => {
      setSyncPolling(false);
      preSyncTimestampRef.current = null;
    }, 15 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [syncPolling]);

  // When sync finishes (timestamp changes), refresh task list
  useEffect(() => {
    if (!syncPolling) return;
    const currentTimestamp = syncStatusData?.summary?.last_timestamp;
    if (!currentTimestamp) return;
    // A new sync log appeared (timestamp differs from what we had before triggering)
    if (currentTimestamp !== preSyncTimestampRef.current) {
      setSyncPolling(false);
      preSyncTimestampRef.current = null;
      // React Query v5 syntax: { queryKey: [...] }
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks-for-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['map-tasks'] });
      // Force immediate refetch (more reliable than invalidate alone)
      queryClient.refetchQueries({ queryKey: ['tasks'] });
      const newCount = syncStatusData?.summary?.last_new_items ?? 0;
      toast({
        title: 'Sincronización completada',
        description: `${newCount} nuevas incidencias cargadas. La lista se ha actualizado.`,
      });
    }
  }, [syncStatusData, syncPolling, queryClient, toast]);

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: syncAPI.syncNow,
    onSuccess: () => {
      // Capture the current timestamp so we can detect when a NEW sync log appears
      preSyncTimestampRef.current = syncStatusData?.summary?.last_timestamp ?? null;
      setSyncPolling(true);          // start aggressive polling (every 8s)
      toast({ title: 'Sincronización iniciada', description: 'Los datos se están actualizando desde SCMejora. Puede tardar unos minutos.' });
      // Immediate status check
      refetchSyncStatus();
    },
    onError: () => {
      toast({ title: 'Error de sincronización', description: 'No se pudo iniciar la sincronización', variant: 'destructive' });
    },
  });

  // ArcGIS Sync mutation
  const arcgisSyncMutation = useMutation({
    mutationFn: syncAPI.syncToArcGIS,
    onSuccess: () => {
      toast({
        title: 'Sincronización ArcGIS iniciada',
        description: 'Los datos se están enviando a ArcGIS Online',
      });
    },
    onError: () => {
      toast({
        title: 'Error de sincronización ArcGIS',
        description: 'No se pudo sincronizar con ArcGIS',
        variant: 'destructive',
      });
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: exportAPI.exportToExcel,
    onSuccess: (response) => {
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      downloadBlob(blob, `tareas_scmejora_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast({
        title: 'Exportación exitosa',
        description: 'Los datos se han exportado a Excel correctamente',
      });
    },
    onError: () => {
      toast({
        title: 'Error de exportación',
        description: 'No se pudo exportar los datos',
        variant: 'destructive',
      });
    },
  });

  const handleSync = () => {
    setShowSyncConfirm(true);
  };

  const confirmSync = () => {
    syncMutation.mutate();
    setShowSyncConfirm(false);
  };

  const handleArcGISSync = () => {
    setShowArcGISSyncConfirm(true);
  };

  const confirmArcGISSync = () => {
    arcgisSyncMutation.mutate();
    setShowArcGISSyncConfirm(false);
  };

  const handleExport = () => {
    setShowExportConfirm(true);
  };

  const confirmExport = () => {
    exportMutation.mutate(filters);
    setShowExportConfirm(false);
  };

  const handleToggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => 
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  // Quick filter for high priority tasks
  const handleFilterHighPriority = (priority) => {
    // Map numeric priorities to string values used in SCMejora
    const priorityMap = {
      4: 'alta',
      5: 'urgente'  // or 'muy_alta', 'critica'
    };
    setFilters({ priority: [priorityMap[priority] || priority] });
    setDismissedAlert(true);
  };

  const handleFilterAllHighPriority = () => {
    // Filter for both alta and urgente/muy_alta
    setFilters({ priority: ['alta', 'urgente', 'muy_alta', 'critica'] });
    setDismissedAlert(true);
  };

  const handleClearSelection = () => {
    setSelectedTaskIds([]);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border-b border-border">
        {/* Left: Logo + Title */}
        <div className="flex items-center space-x-3 min-w-0">
          <div className="bg-gradient-to-r from-[#003D5C] to-[#005580] px-3 py-1.5 rounded-lg flex-shrink-0">
            <img src="/valoriza-logo.svg" alt="Valoriza" className="h-7 w-auto" />
          </div>
          <div className="h-5 w-px bg-border flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base font-bold text-foreground leading-tight">Incidencias Santa Cruz</h1>
            <p className="text-xs text-muted-foreground leading-tight">{user.nombre} {user.apellido}</p>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">

          {/* Filters */}
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
            className="h-8"
          >
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Filtros
          </Button>

          {/* Toggle completadas */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncludeCompleted(!includeCompleted)}
            data-testid="toggle-completed"
            className={`h-8 ${includeCompleted ? 'bg-green-50 border-green-500 text-green-700' : ''}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            {includeCompleted ? 'Todas' : 'Activas'}
          </Button>

          {/* Nueva Incidencia */}
          <Button
            size="sm"
            onClick={() => setShowCreateIncident(true)}
            data-testid="create-incident-button"
            className="h-8 bg-[#008040] hover:bg-[#006030]"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Nueva
          </Button>

          {/* Sincronización dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`h-8 ${syncStatusData?.summary?.last_pages_with_errors?.length > 0 ? 'border-orange-400 text-orange-600' : ''}`}
                data-testid="sync-dropdown"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${(syncMutation.isPending || arcgisSyncMutation.isPending || syncPolling) ? 'animate-spin' : ''}`} />
                Sincronizar
                <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Sincronización</DropdownMenuLabel>
              {syncStatusData?.summary && (
                <div className="px-3 py-2 border-b text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Último sync:</span>
                    <span className={`font-medium ${syncPolling ? 'text-blue-500' : syncStatusData.summary.last_status === 'success' ? 'text-green-600' : syncStatusData.summary.last_status === 'partial' ? 'text-orange-500' : 'text-red-500'}`}>
                      {syncPolling ? '⟳ En curso...' : syncStatusData.summary.last_status === 'success' ? '✓ OK' : syncStatusData.summary.last_status === 'partial' ? '⚠ Parcial' : '✗ Error'}
                    </span>
                  </div>
                  {syncStatusData.last_sync?.error && (
                    <div className="text-red-500 text-xs bg-red-50 dark:bg-red-900/20 rounded px-1 py-0.5 break-words">
                      {String(syncStatusData.last_sync.error).slice(0, 100)}
                    </div>
                  )}
                  {syncStatusData.summary.last_timestamp && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Hora:</span>
                      <span>{new Date(syncStatusData.summary.last_timestamp).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nuevas:</span>
                    <span className="font-medium text-green-600">+{syncStatusData.summary.last_new_items}</span>
                  </div>
                  {syncStatusData.summary.newest_fecha_incidencia && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Incid. más reciente:</span>
                      <span className="font-medium">{String(syncStatusData.summary.newest_fecha_incidencia).slice(0, 10)}</span>
                    </div>
                  )}
                  {syncStatusData.summary.last_excluded_items > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Excluidas:</span>
                      <span className="text-orange-500">{syncStatusData.summary.last_excluded_items}</span>
                    </div>
                  )}
                  {syncStatusData.summary.last_pages_with_errors?.length > 0 && (
                    <div className="text-orange-500 text-xs">⚠ {syncStatusData.summary.last_pages_with_errors.length} páginas con error</div>
                  )}
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSync} disabled={syncMutation.isPending || syncPolling} data-testid="sync-button">
                <RefreshCw className="w-4 h-4 mr-2" />
                {syncMutation.isPending ? 'Iniciando...' : syncPolling ? 'Sincronizando...' : 'Sync SCMejora → BD'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleArcGISSync} disabled={arcgisSyncMutation.isPending} data-testid="arcgis-sync-button">
                <Globe className="w-4 h-4 mr-2 text-[#008040]" />
                {arcgisSyncMutation.isPending ? 'Sincronizando...' : 'Sync ArcGIS Online'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Informes & Exportar dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="reports-dropdown">
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                Informes
                <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Análisis e Informes</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowAnalytics(true)} data-testid="analytics-button">
                <Activity className="w-4 h-4 mr-2 text-blue-600" />
                Analytics Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowAIReportModal(true)} data-testid="generate-ai-report">
                <Sparkles className="w-4 h-4 mr-2 text-[#008040]" />
                Informes con IA
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExport} disabled={exportMutation.isPending} data-testid="export-button">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exportMutation.isPending ? 'Exportando...' : 'Exportar Excel'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Admin actions */}
          {user.rol === 'admin' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 bg-primary/5" data-testid="admin-dropdown">
                  <Users className="w-3.5 h-3.5 mr-1.5" />
                  Admin
                  <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Administración</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowUserManagement(true)} data-testid="user-management-button">
                  <Users className="w-4 h-4 mr-2" />
                  Gestión de Usuarios
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowApiLogs(true)} data-testid="api-logs-button">
                  <FileText className="w-4 h-4 mr-2" />
                  Logs de API
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Notification Bell */}
          <NotificationBell />

          {/* ArcGIS sync status micro-indicator */}
          <ArcGISSyncStatus isSyncing={arcgisSyncMutation.isPending} />

          {/* Mobile View */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.href = '/mobile'}
            data-testid="mobile-view-button"
            className="h-8 border-orange-400 text-orange-600 hover:bg-orange-50"
          >
            <Map className="w-3.5 h-3.5 mr-1.5" />
            Móvil
          </Button>

          {/* Dark mode toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            data-testid="dark-mode-toggle"
            className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground"
            title={isDark ? 'Modo claro' : 'Modo oscuro'}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Logout */}
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button" className="h-8">
            <LogOut className="w-3.5 h-3.5 mr-1.5" />
            Salir
          </Button>
        </div>
      </header>

      {/* High Priority Alert Banner */}
      {showHighPriorityAlert && (
        <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-3 flex items-center justify-between animate-pulse-slow" data-testid="high-priority-alert">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 animate-bounce" />
              <span className="font-bold text-lg">¡ALERTA!</span>
            </div>
            <span className="text-sm">
              Hay <strong>{highPriorityTasks.count}</strong> incidencia{highPriorityTasks.count !== 1 ? 's' : ''} de alta prioridad
              {highPriorityTasks.p5 > 0 && (
                <span className="ml-1">
                  (<strong>{highPriorityTasks.p5}</strong> P5 Urgente)
                </span>
              )}
              {highPriorityTasks.p4 > 0 && (
                <span className="ml-1">
                  (<strong>{highPriorityTasks.p4}</strong> P4 Alta)
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {highPriorityTasks.p5 > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleFilterHighPriority(5)}
                className="bg-white text-red-600 hover:bg-red-50 font-semibold"
                data-testid="filter-p5-button"
              >
                Ver P5 Urgente ({highPriorityTasks.p5})
              </Button>
            )}
            {highPriorityTasks.p4 > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleFilterHighPriority(4)}
                className="bg-white text-orange-600 hover:bg-orange-50 font-semibold"
                data-testid="filter-p4-button"
              >
                Ver P4 Alta ({highPriorityTasks.p4})
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleFilterAllHighPriority}
              className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold"
              data-testid="filter-all-high-button"
            >
              Ver Todas ({highPriorityTasks.count})
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setDismissedAlert(true)}
              className="text-white hover:bg-white/20 h-8 w-8"
              data-testid="dismiss-alert-button"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Stats Panel */}
      {stats && <StatsPanel stats={stats} />}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Tasks List */}
        <div className="w-96 bg-white dark:bg-gray-900 border-r border-border flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar tareas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground bg-background placeholder:text-muted-foreground"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <FiltersPanel
              filters={filters}
              onFiltersChange={setFilters}
              onClose={() => setShowFilters(false)}
            />
          )}

          {/* Critical Zones Widget */}
          <CriticalZonesWidget
            onZoneClick={(district) => {
              setFilters(prev => ({ ...prev, distrito: district }));
              setShowFilters(false);
            }}
          />

          {/* Tasks List */}
          <div className="flex-1 overflow-y-auto flex flex-col">
            <TasksList
              tasks={tasksData?.data || []}
              loading={tasksLoading}
              selectedTaskId={selectedTask?.id}
              onSelectTask={setSelectedTask}
              selectedTaskIds={selectedTaskIds}
              onToggleTaskSelection={handleToggleTaskSelection}
            />
            {/* Pagination Controls */}
            {tasksData && tasksData.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-muted/20 text-xs shrink-0" data-testid="pagination-controls">
                <span className="text-muted-foreground">
                  Mostrando {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, tasksData.total)} de <strong>{tasksData.total.toLocaleString('es-ES')}</strong>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    data-testid="pagination-prev"
                    className="px-2 py-1 rounded border border-border bg-white dark:bg-gray-900 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹ Anterior
                  </button>
                  <span className="px-3 py-1 font-medium">
                    {currentPage} / {tasksData.total_pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(tasksData.total_pages, p + 1))}
                    disabled={currentPage >= tasksData.total_pages}
                    data-testid="pagination-next"
                    className="px-2 py-1 rounded border border-border bg-white dark:bg-gray-900 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Map View */}
        <div className="flex-1 relative">
          {/* Map filters toggle button */}
          <button
            onClick={() => setShowMapFilters(v => !v)}
            data-testid="map-filter-toggle"
            className={`absolute top-3 right-3 z-[99] flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium shadow-lg border transition-all ${
              hasMapFilters
                ? 'bg-primary text-white border-primary'
                : 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm text-foreground border-border hover:border-primary'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filtrar mapa
            {hasMapFilters && (
              <span className="bg-white/30 rounded-full px-1.5 text-[10px] font-bold">
                {(mapFilters.tipo ? 1 : 0) + ((mapFilters.date_from || mapFilters.date_to) ? 1 : 0)}
              </span>
            )}
          </button>

          {/* Map filters panel (floating overlay) */}
          {showMapFilters && (
            <MapFiltersBar
              filters={mapFilters}
              onChange={(newFilters) => {
                setMapFilters(newFilters);
                setShowMapFilters(false);
              }}
              onClose={() => setShowMapFilters(false)}
            />
          )}

          {/* Map task count badge when filtered */}
          {hasMapFilters && (
            <div className="absolute bottom-3 left-3 z-[99] bg-primary text-white text-xs px-3 py-1.5 rounded-full shadow-lg font-medium" data-testid="map-filter-badge">
              {mapTasksData?.total ? `${mapTasksData.total.toLocaleString('es-ES')} tareas en mapa` : 'Cargando mapa...'}
            </div>
          )}

          <MapView
            tasks={hasMapFilters ? (mapTasksData?.data || []) : (tasksData?.data || [])}
            selectedTask={selectedTask}
            onSelectTask={setSelectedTask}
          />
        </div>

        {/* Task Details Panel */}
        {selectedTask && (
          <TaskDetailsErrorBoundary onClose={() => setSelectedTask(null)}>
            <TaskDetails
              task={liveSelectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={() => {
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
                setSelectedTask(null);
              }}
            />
          </TaskDetailsErrorBoundary>
        )}
      </div>

      {/* User Management Dialog */}
      <UserManagement
        open={showUserManagement}
        onClose={() => setShowUserManagement(false)}
        currentUser={user}
      />

      {/* API Logs Viewer */}
      <ApiLogsViewer
        open={showApiLogs}
        onClose={() => setShowApiLogs(false)}
      />

      {/* Sync Confirm */}
      <ConfirmDialog
        open={showSyncConfirm}
        onOpenChange={setShowSyncConfirm}
        title="Confirmar sincronización"
        description="¿Deseas sincronizar los datos con SCMejora? Actualizará todas las tareas con la información más reciente."
        confirmText="Sí, sincronizar"
        cancelText="Cancelar"
        onConfirm={confirmSync}
        variant="info"
        isLoading={syncMutation.isPending}
      />

      <ConfirmDialog
        open={showExportConfirm}
        onOpenChange={setShowExportConfirm}
        title="Confirmar exportación"
        description={`¿Deseas exportar ${tasksData?.total || 0} tareas a Excel con los filtros actuales?`}
        confirmText="Sí, exportar"
        cancelText="Cancelar"
        onConfirm={confirmExport}
        variant="info"
        isLoading={exportMutation.isPending}
      />

      <ConfirmDialog
        open={showArcGISSyncConfirm}
        onOpenChange={setShowArcGISSyncConfirm}
        title="Sincronizar con ArcGIS Online"
        description={`¿Deseas enviar ${tasksData?.total || 0} incidencias a la capa de ArcGIS Online?`}
        confirmText="Sí, sincronizar"
        cancelText="Cancelar"
        onConfirm={confirmArcGISSync}
        variant="info"
        isLoading={arcgisSyncMutation.isPending}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedTaskIds={selectedTaskIds}
        onClearSelection={handleClearSelection}
      />

      {/* Create Incident Modal */}
      {showCreateIncident && (
        <CreateIncidentModalV2
          onClose={() => setShowCreateIncident(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
          }}
        />
      )}

      {/* Report Dialog (legacy, remove later) */}

      {/* AI Reports Modal */}
      {showAIReportModal && (
        <AIReportModal onClose={() => setShowAIReportModal(false)} />
      )}

      {/* Analytics Dashboard Modal */}
      {showAnalytics && (
        <AnalyticsDashboard onClose={() => setShowAnalytics(false)} />
      )}

    </div>
  );
}
